import type { Icon } from "@phosphor-icons/react";
import type { ApiClient } from "~/api/client";
import type { Abilities, Viewer } from "~/api/types";
import type { ShellStore } from "~/shell/store";

/** Which surface invoked the action. Recorded per invocation so analytics can tell them apart. */
export type ActionSource = "commandbar" | "contextmenu" | "button" | "shortcut";

export interface ActionContext {
  t: (key: string, options?: Record<string, unknown>) => string;
  navigate: (to: string) => void;
  api: ApiClient;
  /** Undefined before authentication — actions that need a viewer must say so in `visible`. */
  viewer?: Viewer | undefined;
  abilities: (entityId: string) => Abilities;
  /**
   * W10 §2 — the viewer's stars, as a lookup plus the one verb that changes them.
   *
   * Optional and shaped exactly like `abilities` for the same reason: an action must be able to
   * ASK a question about an entity without importing a store or a query cache. A context
   * assembled outside the app (a test, a headless surface) simply has no stars, and the star
   * actions hide themselves rather than guess.
   */
  stars?: {
    /** The star row's id, or undefined when this entity is not starred. */
    idFor: (entityId: string) => string | undefined;
    /** Resolves the current list before writing, so a stale lookup cannot create a duplicate. */
    toggle: (target: { documentId?: string; collectionId?: string }) => void;
  };
  shell: ShellStore;
  source: ActionSource;
  /** e.g. the item under the context menu that opened this action list. */
  activeEntityId?: string | undefined;
}

/** Any field that may need the context to be resolved — a title, a section, a child list. */
export type Dyn<T> = T | ((context: ActionContext) => T);

export interface Action {
  /** Stable slug: "theme.dark", "items.new". Used by analytics and the recents list. */
  id: string;
  name: Dyn<string>;
  /** Untranslated, for telemetry. Defaults to `id`. */
  analyticsName?: string;
  /** Command-bar grouping, translated. */
  section: Dyn<string>;
  icon?: Icon;
  /** Extra search terms for the command bar. */
  keywords?: string;
  /** Declarative binding, e.g. ["Meta", "k"] — registered once by shell/shortcuts.ts. */
  shortcut?: string[];
  /**
   * Fire the binding even while the focus is in an input or a contenteditable.
   *
   * Off by default, because typing "d" in a search box must never repaint the app. On for the
   * Ask bar: a chord whose whole purpose is "turn what I am typing into an intent" that stops
   * working the moment you type is not a shortcut, it is a trap.
   */
  allowInInput?: boolean;
  visible?: (context: ActionContext) => boolean;
  /** Shows a ✓ in menus — used by the theme and language pickers. */
  selected?: (context: ActionContext) => boolean;
  /** Destructive: rendered in the danger colour and confirmed before it runs. */
  dangerous?: boolean;
  perform?: (context: ActionContext) => void | Promise<void>;
  children?: Dyn<Action[]>;
}

export function resolve<T>(value: Dyn<T>, context: ActionContext): T {
  return typeof value === "function" ? (value as (c: ActionContext) => T)(context) : value;
}

export function actionChildren(action: Action, context: ActionContext): Action[] {
  return action.children ? resolve(action.children, context) : [];
}

export function isVisible(action: Action, context: ActionContext): boolean {
  return action.visible ? action.visible(context) === true : true;
}

/** Re-exported so `actions/types` is the one import a surface needs. Defined in foundation. */
export type { MenuItem } from "~/foundation/components/Menu";
